import React from "react";
import { Box, Typography, CardActionArea, Card } from "@mui/material";
import { ProgramTemplateDto } from "@growth-machine-llc/stridist-api-client";
import { makeStyles } from "@mui/styles";
import { useTheme } from "@mui/material";
import { ProgramTemplateMenu } from "./ProgramTemplateMenu";
import { ProgramTemplateTypeBadge } from "./ProgramTemplatePrivacyBadge";
import { ProgramCategoryBadge } from "./ProgramCategoryBadge";
import SeeMoreNotes from "../training-summary/SeeMoreNotes";
import { useUpdateTemplateCategoryMutation } from "../../hooks/useUpdateTemplateCategoryMutation";

const useStyles = makeStyles((theme) => ({
  actionArea: {
    padding: theme.spacing(1.5),
    justifyContent: "space-between",
  },
  thumbnail: {
    height: "100%",
    maxHeight: 120,
    aspectRatio: "1/1",
    objectFit: "cover",
    borderRadius: theme.spacing(1),
    flexShrink: 0,
  },
  thumbnailPlaceholder: {
    height: "100%",
    maxHeight: 120,
    aspectRatio: "1/1",
    borderRadius: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  name: {
    fontWeight: 600,
    fontSize: 16,
    marginBottom: theme.spacing(0.5),
  },
  category: {
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    marginBottom: theme.spacing(0.5),
  },
  description: {
    fontWeight: 400,
    color: theme.palette.text.secondary,
  },
  info: {
    height: "100%",
    display: "flex",
    flexDirection: "column-reverse",
    justifyContent: "space-between",
  },
  mobileCard: {
    marginBottom: theme.spacing(2),
    boxShadow: theme.shadows[2],
    position: "relative",
  },
  menuButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 1,
  },
}));

export interface ProgramsTableCardsViewProps {
  template: ProgramTemplateDto;
  onSelectedTemplate?: (template: ProgramTemplateDto) => void;
  className?: string;
}

export function ProgramsTableCardsView(props: ProgramsTableCardsViewProps) {
  const { template, onSelectedTemplate, className, ...other } = props;
  const s = useStyles();
  const theme = useTheme();

  const canEdit = template.canEdit;

  const { updateTemplateCategory } = useUpdateTemplateCategoryMutation();

  const handleSelectedTemplate = React.useCallback(() => {
    if (onSelectedTemplate) {
      onSelectedTemplate(template);
    }
  }, [onSelectedTemplate, template]);

  return (
    <Card className={`${s.mobileCard} ${className ?? ""}`} {...other}>
      <Box className={s.menuButton} onClick={(e) => e.stopPropagation()}>
        <ProgramTemplateMenu template={template} />
      </Box>
      <CardActionArea className={s.actionArea} onClick={handleSelectedTemplate}>
        <Box display="flex" justifyContent="space-between" gap={2}>
          <Box display="flex" alignItems="flex-start" flexShrink={0}>
            {template.image ? (
              <Box
                component="img"
                src={template.image}
                alt={template.name}
                className={s.thumbnail}
              />
            ) : (
              <Box
                className={s.thumbnailPlaceholder}
                sx={{
                  backgroundColor: theme.palette.grey[200],
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  No image
                </Typography>
              </Box>
            )}
          </Box>

          <Box flexGrow={1} display="flex" flexDirection="column">
            <Typography className={s.name}>{template.name}</Typography>
            <Box className={s.info}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={0.5}
              >
                <ProgramTemplateTypeBadge
                  template={template}
                  size={24}
                  showLabel={true}
                />
                <ProgramCategoryBadge
                  template={template}
                  onCategoryChange={(category) =>
                    updateTemplateCategory({ id: template.id, category })
                  }
                  readonly={!canEdit}
                />
              </Box>
              {template.description && (
                <Typography variant="caption" className={s.description}>
                  <SeeMoreNotes rows={2}>{template.description}</SeeMoreNotes>
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
}
